import type { UniverseConditionModel, UniverseSearchModel } from '../UniverseCreationModel';
import { FlagConditionModel } from './FlagConditionModel';
import { indicatorEnumeratedValues } from './indicatorEnumeratedValues';
import { RangeConditionModel } from './RangeConditionModel';
import { SetConditionModel } from './SetConditionModel';
import { canConvert, FieldTypeCategory } from '@thinkalpha/language-services';
import type { IndicatorPreset, ConcreteIndicator } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { FormulaService } from 'src/services/FormulaService';

export function getPresetsForSearchModel(
    presets: IndicatorPreset[],
    searchModel: UniverseSearchModel,
): (IndicatorPreset & { match: { type: 'formula' } })[] {
    // filter down to just presets are SelfAliasIndicatorFormula
    const presetsFormula = presets.filter(
        (x): x is typeof x & { match: { type: 'formula' } } => x.match.type === 'formula',
    );

    switch (searchModel) {
        case 'screener':
            return presetsFormula.filter((x) => x.screener);
        case 'backtest':
            return presetsFormula.filter((x) => !!x.backtestData?.universe);
        default:
            throw new UnreachableCaseError(searchModel);
    }
}

export function conditionFromIndicator(
    indicator: ConcreteIndicator,
    searchModel: UniverseSearchModel,
    formulaService: FormulaService,
): UniverseConditionModel {
    const selection = getPresetsForSearchModel(indicator.presets, searchModel)[0]?.match;

    switch (true) {
        case canConvert(indicator.dataType, FieldTypeCategory.Boolean):
            return new FlagConditionModel({
                id: indicator.id,
                enabled: true,
                indicator,
                selection,
                searchModel,
                formulaService,
            });
        case canConvert(indicator.dataType, FieldTypeCategory.Double):
            return new RangeConditionModel({
                id: indicator.id,
                enabled: true,
                indicator,
                max: null,
                min: null,
                operation: 'between',
                selection,
                searchModel,
                formulaService,
            });
        case canConvert(indicator.dataType, FieldTypeCategory.String) || indicator.enumeratedReturnValues:
            return new SetConditionModel({
                id: indicator.id,
                enabled: true,
                indicator,
                enumeratedValues: indicatorEnumeratedValues(indicator, searchModel),
                values: [],
                selection,
                searchModel,
                formulaService,
            });
        default:
            throw new Error('Unsupported indicator type');
    }
}
